/*
 * File: CustomTextField.tsx
 * Project: a-a
 * File Created: Wednesday, 9th October 2024 12:14:59 am
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Thursday, 18th September 2025 6:07:38 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import React, { forwardRef, ReactElement, ReactNode } from 'react';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import zeplinColor from '../../../theme/Color';
import { InputAdornment, SxProps, useMediaQuery, useTheme } from '@mui/material';

type CustomTextFieldType = {
  style?: React.CSSProperties | SxProps | undefined;
  text: string;
  error?: string | boolean;
  label: string;
  type?: string;
  name: string;
  onBlur?: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disable?: boolean;
  required?: boolean;
  tabindex?: number;
  multiline?: boolean;
  startIcon?: boolean;
  // endAdornment?:React.ReactNode
  close?: boolean;
  handleClear?: () => void;
  endIcon?: ReactElement;
  // ref?: React.MutableRefObject<null | HTMLDivElement>;
  max?: number;
  placeHolder?: string;
  onEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  form?: boolean;
};

const CustomField = styled(TextField)(({ theme }) => ({
  '&.MuiFormControl-root.MuiTextField-root': {
    // '& .MuiFormLabel-root.MuiInputLabel-root': {
    //   color: zeplinColor.Neutral9,
    // },
    // '& .MuiFormLabel-root.MuiInputLabel-root.Mui-error': {
    //   color: zeplinColor.Error,
    // },
    // '& .MuiFormLabel-root.MuiInputLabel-root:focus': {
    //   color: zeplinColor.Brand1,
    // },
    // '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-error': {
    //   border: `none`,
    //   '& .MuiOutlinedInput-notchedOutline': {
    //     border: `1px solid ${zeplinColor.Error}`,
    //   },
    // },
    // '& .MuiInputBase-root.MuiOutlinedInput-root:not(.Mui-error)': {
    //   border: `none`,
    //   '& .MuiOutlinedInput-notchedOutline': {
    //     border: `1px solid ${zeplinColor.Neutral9}`,
    //   },
    // },

    '& .MuiFormHelperText-root.Mui-error ': {
      //   position:'absolute',
      //   bottom:'-1.5rem'
      marginTop: '0px',
      height: '0px',
      marginBottom: '1.5rem',
    },
    '& .MuiFormLabel-asterisk': {
      color: zeplinColor.Error,
    },

    // '& .MuiInputBase-root':{
    //     height:'80px'
    // }
  },
}));

const CustomTextField = forwardRef<HTMLDivElement, CustomTextFieldType>(
  (
    {
      style,
      text,
      error,
      label,
      type = 'text',
      onBlur,
      onChange,
      name,
      disable = false,
      required = false,
      tabindex,
      multiline = false,
      close = false,
      handleClear,
      endIcon,
      startIcon,
      // ref,
      max,
      placeHolder = '',
      onEnter,
      form = false,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('xl'));
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // console.log(e);
      if (e.code === 'Enter' && onEnter) {
        onEnter(e);
      }
    };

    return (
      <>
        <CustomField
          onClick={(e) => {
            e.stopPropagation();
          }}
          ref={ref}
          required={required}
          disabled={disable}
          onKeyDown={handleKeyDown}
          name={name}
          size={isXs && form ? 'small' : 'medium'}
          onBlur={onBlur}
          label={label}
          type={type}
          placeholder={placeHolder ? placeHolder : label}
          error={error ? true : false}
          helperText={error ? error : ''}
          sx={style ? { ...style } : {}}
          value={text}
          onChange={(e: any) => {
            let val = +e.target.value;
            if (type === 'number' && max && val > max) {
              return;
            }
            onChange(e);
          }}
          tabIndex={tabindex}
          multiline={multiline}
          rows={4}
          slotProps={{
            inputLabel: {
              size: isXs && form ? 'small' : 'medium',
              sx: { fontSize: isXs && form ? 14 : 16 },
            },
          }}
          InputProps={{
            startAdornment:
              label === 'Rate' || label === 'Sales price' || label === 'Amount' ? (
                <InputAdornment position='start' sx={{ '& p': { color: zeplinColor.BasicBlack } }}>
                  $
                </InputAdornment>
              ) : startIcon ? (
                <InputAdornment position='start' sx={{ '& p': { color: zeplinColor.BasicBlack } }}>
                  <img
                    width={20}
                    height={20}
                    src={'/assets/clear-input.svg'}
                    alt='clear-input'
                    style={{
                      borderRadius: '14px',
                      objectFit: 'fill',
                      cursor: 'pointer',
                    }}
                    // onClick={handleClear}
                    // style={{ imageRendering: 'pixelated',  }}
                  />
                </InputAdornment>
              ) : (
                ''
              ),
            endAdornment: endIcon
              ? endIcon
              : close && (
                  <InputAdornment position='end'>
                    <img
                      width={20}
                      height={20}
                      src={'/assets/clear-input.svg'}
                      alt='clear-input'
                      style={{
                        borderRadius: '14px',
                        objectFit: 'fill',
                        cursor: 'pointer',
                      }}
                      onClick={handleClear}
                      // style={{ imageRendering: 'pixelated',  }}
                    />
                  </InputAdornment>
                ),
            inputProps: {
              ...(type === 'number'
                ? {
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    min: 0,
                    max: max,

                    // Remove + and - for number input
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === '+' || e.key === '-') {
                        e.preventDefault();
                      }
                    },
                  }
                : {}),
              type: type,
            },
            sx: {
              // height: { xs: 46, xl: 56 },
              '&.MuiOutlinedInput-root': {
                fontSize: isXs && form ? 14 : 16,
              },
              '& input::placeholder': {
                fontSize: isXs && form ? 14 : 16, // Adjust the font size as needed
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                display: 'none',
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            },
          }}
          // endAdornment={endAdornment}
        />
      </>
    );
  },
);

export default CustomTextField;
